import type { ComponentCatalogEntry } from './catalog-types.js';

export const primaryCoreComponentCatalogEntries: readonly ComponentCatalogEntry[] = [
  // Primitives
  {
    slug: 'button',
    title: 'Button',
    area: 'core',
    group: 'Primitives',
    status: 'stable',
    intent: 'A clickable action with six variants mapped to data-variant.',
  },
  {
    slug: 'icon-button',
    title: 'Icon button',
    area: 'core',
    group: 'Primitives',
    status: 'stable',
    intent: 'A square, icon-only action requiring an aria-label; four variants.',
  },
  {
    slug: 'scroll-to-bottom-button',
    title: 'Scroll to bottom button',
    area: 'core',
    group: 'Primitives',
    status: 'stable',
    intent:
      'A compact floating chevron action that appears when a scrollport moves away from its newest content and restores the bottom-pinned view.',
  },
  {
    slug: 'input',
    title: 'Input',
    area: 'core',
    group: 'Inputs',
    status: 'stable',
    intent: 'A single-line text field with an invalid state for inline errors.',
  },
  {
    slug: 'textarea',
    title: 'Textarea',
    area: 'core',
    group: 'Inputs',
    status: 'stable',
    intent: 'A multi-line text field with an invalid state and a default row count.',
  },
  {
    slug: 'select',
    title: 'Select',
    area: 'core',
    group: 'Inputs',
    status: 'stable',
    intent: 'A styled wrapper over the native select with a trailing chevron.',
  },
  {
    slug: 'search',
    title: 'Search',
    area: 'core',
    group: 'Inputs',
    status: 'stable',
    intent: 'An expand-on-focus search field that collapses back to its icon.',
  },
  {
    slug: 'checkbox',
    title: 'Checkbox',
    area: 'core',
    group: 'Inputs',
    status: 'stable',
    intent: 'A checkbox with checked, unchecked, and indeterminate states.',
  },
  {
    slug: 'radio',
    title: 'Radio',
    area: 'core',
    group: 'Inputs',
    status: 'stable',
    intent: 'A single-choice radio group built from RadioGroup and RadioGroupItem.',
  },
  {
    slug: 'toggle',
    title: 'Toggle',
    area: 'core',
    group: 'Inputs',
    status: 'stable',
    intent: 'A switch with a sliding thumb for an immediate on/off setting.',
  },
  {
    slug: 'badge',
    title: 'Badge',
    area: 'core',
    group: 'Primitives',
    status: 'stable',
    intent: 'A small status label with eight tones and an optional leading dot.',
  },
  {
    slug: 'tag',
    title: 'Tag',
    area: 'core',
    group: 'Primitives',
    status: 'stable',
    intent: 'An inline metadata label with three variants.',
  },
  {
    slug: 'avatar',
    title: 'Avatar',
    area: 'core',
    group: 'Primitives',
    status: 'stable',
    intent: 'A circular identity badge, with a group that collapses overflow into +N.',
  },
  {
    slug: 'kbd',
    title: 'Kbd',
    area: 'core',
    group: 'Primitives',
    status: 'stable',
    intent: 'A keyboard key hint that composes into shortcut chords.',
  },
  {
    slug: 'meter',
    title: 'Meter',
    area: 'core',
    group: 'Visualizations',
    status: 'beta',
    intent:
      'A linear ratio bar for a single value against a max (quota, budget, progress); tone signals headroom.',
  },
  {
    slug: 'filter-pill',
    title: 'Filter pill',
    area: 'core',
    group: 'Primitives',
    status: 'stable',
    intent: 'A toggleable filter trigger with active and open states.',
  },
  {
    slug: 'scope-pill',
    title: 'Scope pill',
    area: 'core',
    group: 'Primitives',
    status: 'stable',
    intent: 'A read-only pill labelling the scope something applies to.',
  },

  // Forms
  {
    slug: 'field',
    title: 'Field',
    area: 'core',
    group: 'Forms',
    status: 'stable',
    intent:
      'The canonical form row: label, required asterisk, hint, and error, wired to any control via a render contract.',
  },
  {
    slug: 'calendar',
    title: 'Calendar',
    area: 'core',
    group: 'Inputs',
    status: 'beta',
    intent:
      'A month-grid date picker with selected/today states, prev/next and quick-year navigation, and min/max/future/past validation that disables out-of-range days.',
  },
  {
    slug: 'inline-edit',
    title: 'Inline edit',
    area: 'core',
    group: 'Forms',
    status: 'stable',
    intent:
      'Edit-in-place control for a text, number, or single-selection value, with edit, save, and cancel affordances.',
  },
  {
    slug: 'segmented-control',
    title: 'Segmented control',
    area: 'core',
    group: 'Inputs',
    status: 'stable',
    intent:
      'Single-select control on a recessed track; the pressed segment rises to a raised pill.',
  },
  {
    slug: 'combobox',
    title: 'Combobox',
    area: 'core',
    group: 'Inputs',
    status: 'stable',
    intent:
      'Searchable select with single and multi modes, built on the canonical Popover and a cmdk filter.',
  },
  {
    slug: 'accordion',
    title: 'Accordion',
    area: 'core',
    group: 'Forms',
    status: 'stable',
    intent:
      'Stacked disclosure rows in single (one-open) or multiple modes, with an animated body height.',
  },
  {
    slug: 'selection-list',
    title: 'SelectionList',
    area: 'core',
    group: 'Inputs',
    status: 'stable',
    intent:
      'Searchable grouped multi-select: category pills with counts, group select-all with indeterminate state, and indented item rows with optional badges.',
  },
  {
    slug: 'composer',
    title: 'Composer',
    area: 'core',
    group: 'Forms',
    status: 'beta',
    intent:
      'Chat/agent message input: a compact auto-grow composer with a lower anchored Send action that swaps to Stop while streaming.',
  },
  {
    slug: 'key-value-editor',
    title: 'Key-value editor',
    area: 'core',
    group: 'Forms',
    status: 'stable',
    intent: 'A compact editable table for string key/value pairs with add and remove actions.',
  },
  {
    slug: 'json-code-editor',
    title: 'JSON code editor',
    area: 'core',
    group: 'Forms',
    status: 'beta',
    intent:
      'A controlled JSON text editor with line numbers, folding, syntax tones, and parse diagnostics.',
  },
  {
    slug: 'file-dropzone',
    title: 'File dropzone',
    area: 'core',
    group: 'Forms',
    status: 'stable',
    intent:
      'A drag-and-select file input with a per-file list showing progress, preview, and error/retry.',
  },
  {
    slug: 'file-bundle-editor',
    title: 'File bundle editor',
    area: 'core',
    group: 'Forms',
    status: 'stable',
    intent:
      'A controlled file-bundle workspace with upload actions, a file list, and one active text editor or read-only binary view.',
  },
  {
    slug: 'markdown-editor',
    title: 'Markdown editor',
    area: 'core',
    group: 'Forms',
    status: 'stable',
    intent:
      'A controlled Markdown textarea with a corner accent toggle that swaps between writing and the canonical Markdown preview.',
  },

  // Overlays
  {
    slug: 'dialog',
    title: 'Dialog',
    area: 'core',
    group: 'Overlays',
    status: 'stable',
    intent: 'A modal surface with a built-in focus trap, Escape-to-close, and focus return.',
  },
  {
    slug: 'drawer',
    title: 'Drawer',
    area: 'core',
    group: 'Overlays',
    status: 'stable',
    intent:
      'A floating, rounded right-side panel over a scrim with no close button; dismisses on click-outside or Escape and reflows to a floating bottom sheet on mobile.',
  },
  {
    slug: 'markdown-viewer',
    title: 'Markdown viewer',
    area: 'core',
    group: 'Overlays',
    status: 'beta',
    intent:
      'A floating non-modal reading window that renders Markdown above a page that stays interactive — no scrim.',
  },
  {
    slug: 'confirm-dialog',
    title: 'Confirm dialog',
    area: 'core',
    group: 'Overlays',
    status: 'stable',
    intent: 'An alert dialog for a single decision, with default and danger variants.',
  },
  {
    slug: 'form-dialog',
    title: 'Form dialog',
    area: 'core',
    group: 'Overlays',
    status: 'beta',
    intent:
      'The create/edit modal: a scrollable form body, pinned Cancel/Submit footer, submitting state, and error summary.',
  },
  {
    slug: 'menu',
    title: 'Menu',
    area: 'core',
    group: 'Overlays',
    status: 'stable',
    intent: 'The canonical dropdown menu: sections, dividers, checks, shortcuts, and danger items.',
  },
  {
    slug: 'popover',
    title: 'Popover',
    area: 'core',
    group: 'Overlays',
    status: 'stable',
    intent: 'A non-modal floating panel anchored to a trigger on any of four sides.',
  },
  {
    slug: 'tooltip',
    title: 'Tooltip',
    area: 'core',
    group: 'Overlays',
    status: 'stable',
    intent: 'A hover and focus label that points back at its trigger from any of four sides.',
  },
  {
    slug: 'hint-icon',
    title: 'Hint icon',
    area: 'core',
    group: 'Overlays',
    status: 'stable',
    intent: 'An inline glyph that reveals a toned tooltip to flag and explain a constraint.',
  },
  {
    slug: 'command-palette',
    title: 'Command palette',
    area: 'core',
    group: 'Overlays',
    status: 'stable',
    intent: 'A ⌘K search dialog over grouped commands, with live filtering and an empty state.',
  },
];
